import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const MAX_COMPARE = 4;
const STORAGE_KEY = 'seh.compare.v1';

const CompareContext = createContext(null);

export const CompareProvider = ({ children }) => {
  const [slugs, setSlugs] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.slice(0, MAX_COMPARE) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(slugs));
    } catch {
      /* ignore */
    }
  }, [slugs]);

  const toggle = useCallback((slug) => {
    setSlugs((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug);
      if (prev.length >= MAX_COMPARE) return prev; // full
      return [...prev, slug];
    });
  }, []);

  const remove = useCallback((slug) => {
    setSlugs((prev) => prev.filter((s) => s !== slug));
  }, []);

  const clear = useCallback(() => setSlugs([]), []);

  const isSelected = useCallback((slug) => slugs.includes(slug), [slugs]);

  const value = useMemo(
    () => ({ slugs, toggle, remove, clear, isSelected, max: MAX_COMPARE, count: slugs.length }),
    [slugs, toggle, remove, clear, isSelected]
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
};

export const useCompare = () => {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error('useCompare must be used within CompareProvider');
  return ctx;
};
